import { useFormikContext } from "formik";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DESIGNATION_OPTIONS, type Form8821Data } from "@/features/forms/types";
import { FormikCell, FormikCheckbox, FormikSelect } from "./formik-controls";

/**
 * Form 8821 "Appointee(s)" — like the shared POA representatives table, but with
 * the 8821-specific per-appointee checkboxes the IRS form carries and the mapper
 * prints: "send copies of notices/communications" and new address / phone / fax
 * since the last CAF filing. Contact details fill from the practitioner record
 * server-side; the user toggles which appointees are active and their flags.
 */
export function TiaAppointeesTable() {
  const { values } = useFormikContext<Form8821Data>();
  const reps = values.representatives;

  if (reps.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No appointees are assigned to this client. Add them on the client's
        profile, then reopen this form.
      </p>
    );
  }

  return (
    <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">Active</TableHead>
            <TableHead>Appointee</TableHead>
            <TableHead className="w-48">Designation</TableHead>
            <TableHead className="w-36">Other ID No.</TableHead>
            <TableHead className="w-16">Copies</TableHead>
            <TableHead className="w-20">New addr.</TableHead>
            <TableHead className="w-20">New phone</TableHead>
            <TableHead className="w-16">New fax</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reps.map((rep, i) => (
            <TableRow key={rep.practitioner || i}>
              <TableCell>
                <FormikCheckbox
                  name={`representatives.${i}.active`}
                  aria-label={`Active ${rep.name}`}
                />
              </TableCell>
              <TableCell className="font-medium">{rep.name}</TableCell>
              <TableCell>
                <FormikSelect
                  name={`representatives.${i}.designation`}
                  options={DESIGNATION_OPTIONS}
                  placeholder="Select…"
                  aria-label={`Designation for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikCell
                  name={`representatives.${i}.other_id`}
                  aria-label={`Other ID number for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikCheckbox
                  name={`representatives.${i}.ok_send`}
                  aria-label={`Send copies to ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikCheckbox
                  name={`representatives.${i}.new_address`}
                  aria-label={`New address for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikCheckbox
                  name={`representatives.${i}.new_phone`}
                  aria-label={`New phone for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikCheckbox
                  name={`representatives.${i}.new_fax`}
                  aria-label={`New fax for ${rep.name}`}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
